import { Component, OnDestroy, OnInit, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';
import { UsersService } from '../../services/users.service';
import { OmsService } from '../../services/oms.service';
import { OrdersService } from '../../services/orders.service';
import { WarehouseService } from '../warehouse.service';

interface BinGroup {
	bin: string;
	lines: any[];
	done: number;
}

/**
 * Pick / check station — the floor screen. Built thumb-first for an iPad on a
 * cart, and it still works on a phone.
 *
 * One component serves both stages; the route's `data.stage` decides which.
 * Picking counts pick → picked; checking counts picked → loaded, so a checker
 * is never asked to find quantity a short pick never produced. The server sends
 * the same JSON shape either way (`target` / `done`), so nothing below has to
 * branch on stage except the wiring: which socket event carries the count,
 * which endpoint closes the stage, and what the labels say.
 *
 * Nothing new happens on the write side — counts go out over the existing
 * `orderitempick` / `orderitemcheck` socket events into apir/oms/picking/update
 * and /updatechecked, and close-out runs /pickcomplete or /checkedcomplete,
 * exactly as the legacy screens do. Only the optional auto-invoice afterwards
 * is new, and the server owns that decision.
 */
@Component({
	selector: 'app-pick-station',
	templateUrl: './pick-station.component.html',
	styleUrls: ['./pick-station.component.scss'],
})
export class PickStationComponent implements OnInit, OnDestroy {

	// The queue and the order view each render their own scan bar; only one is
	// alive at a time, so focus goes to whichever ViewChild resolved.
	@ViewChild('dwSink') dwSinkQueue: ElementRef;
	@ViewChild('dwSinkOrder') dwSinkOrder: ElementRef;
	@ViewChild('manualInput') manualInputQueue: ElementRef;
	@ViewChild('manualInputOrder') manualInputOrder: ElementRef;

	readonly CHAT_ROOM = 'OrderBoard';

	/** 'pick' or 'check' — set from the route, fixed for the life of the screen. */
	stage: 'pick' | 'check' = 'pick';

	user: any = false;
	location = '';

	mode: 'queue' | 'order' = 'queue';
	loading = true;
	error = '';

	settings: any = {
		auto_invoice_on_pick: false,
		auto_invoice_on_check: false,
		auto_invoice_requires_check: false,
		roll_width: 80,
	};

	printing = false;

	// Queue
	rows: any[] = [];
	stats: any = { to_pick: 0, in_progress: 0, ready: 0, short: 0, late: 0 };
	search = '';
	filter: 'all' | 'mine' | 'open' | 'ready' = 'all';

	// Order
	orderno: any = null;
	header: any = null;
	totals: any = { line_count: 0, lines_done: 0, bin_count: 0, qty_pick: 0, qty_picked: 0, pct: 0 };
	lines: any[] = [];
	binGroups: BinGroup[] = [];
	expandedDetail = false;
	activePickId: any = null;
	completing = false;

	// Scanning — hidden sink keeps the soft keyboard down for hardware scanners.
	scanMode: 'scan' | 'manual' = 'scan';
	keyBuffer = '';
	lastScanned = '';
	feedback: { text: string, tone: string }[] = [];

	private scanTimer: any = null;
	private feedbackTimer: any = null;
	private refreshTimer: any = null;
	private echoTimer: any = null;
	private userSub: any = null;
	private paramSub: any = null;
	private inflight: any = null;

	constructor(
		public router: Router,
		private route: ActivatedRoute,
		private warehouseService: WarehouseService,
		private ordersService: OrdersService,
		private omsService: OmsService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
		private usersService: UsersService,
		private cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.stage = this.route.snapshot.data && this.route.snapshot.data['stage'] === 'check' ? 'check' : 'pick';

		this.userSub = this.globalSearchService.user.subscribe((u: any) => {
			if (!u) { return; }
			this.user = u;
			this.location = u.user && u.user.defaultlocation ? u.user.defaultlocation.loccode : '';
			this.connect();
			this.route.params.subscribe(p => this.enter(p['orderno']));
		});

		this.refreshTimer = setInterval(() => {
			if (this.mode === 'queue') { this.loadQueue(true); }
		}, 30000);
	}

	ngOnDestroy(): void {
		[this.scanTimer, this.feedbackTimer, this.echoTimer].forEach(t => { if (t) { clearTimeout(t); } });
		if (this.refreshTimer) { clearInterval(this.refreshTimer); }
		if (this.userSub) { this.userSub.unsubscribe(); }
		if (this.paramSub) { this.paramSub.unsubscribe(); }
		if (this.inflight) { this.inflight.unsubscribe(); }
	}

	private enter(orderno: any) {
		if (orderno && `${orderno}` !== '') {
			this.orderno = orderno;
			this.mode = 'order';
			this.loadOrder();
		} else {
			this.orderno = null;
			this.mode = 'queue';
			this.header = null;
			this.lines = [];
			this.binGroups = [];
			this.loadQueue();
		}
	}

	// ── Queue ────────────────────────────────────────────────────────────────

	loadQueue(silent = false) {
		if (!silent) { this.loading = true; }
		if (this.inflight) { this.inflight.unsubscribe(); }

		this.inflight = this.warehouseService.getPickBoard(this.location, this.stage).subscribe({
			next: (r: any) => {
				this.loading = false;
				this.inflight = null;
				if (!r || r.success !== true) {
					this.error = (r && r.message) ? r.message : 'Could not load the picking queue.';
					return;
				}
				this.error = '';
				this.rows = r.orders || [];
				this.stats = r.stats || this.stats;
				if (r.settings) { this.settings = r.settings; }
			},
			error: () => {
				this.loading = false;
				this.inflight = null;
				this.error = 'Picking queue is not reachable.';
			},
		});
	}

	get me(): string {
		return this.user && this.user.user ? this.user.user.userid : '';
	}

	get visibleRows(): any[] {
		const term = this.search.trim().toLowerCase();
		return this.rows.filter(r => {
			if (this.filter === 'mine' && r.claimed_user !== this.me) { return false; }
			if (this.filter === 'open' && r.bucket === 'ready') { return false; }
			if (this.filter === 'ready' && r.bucket !== 'ready') { return false; }
			if (!term) { return true; }
			return `${r.orderno}`.includes(term)
				|| `${r.customer || ''}`.toLowerCase().includes(term)
				|| `${r.city || ''}`.toLowerCase().includes(term)
				|| `${r.customerref || ''}`.toLowerCase().includes(term);
		});
	}

	countFor(f: string): number {
		if (f === 'mine') { return this.rows.filter(r => r.claimed_user === this.me).length; }
		if (f === 'open') { return this.rows.filter(r => r.bucket !== 'ready').length; }
		if (f === 'ready') { return this.rows.filter(r => r.bucket === 'ready').length; }
		return this.rows.length;
	}

	setFilter(f: any) {
		this.filter = f;
	}

	get basePath(): string {
		return this.stage === 'check' ? '/warehouse/check-station' : '/warehouse/pick-station';
	}

	openOrder(row: any) {
		this.router.navigate([this.basePath, row.orderno]);
	}

	backToQueue() {
		this.router.navigate([this.basePath]);
	}

	// ── Order ────────────────────────────────────────────────────────────────

	loadOrder(silent = false) {
		if (!silent) { this.loading = true; }
		if (this.inflight) { this.inflight.unsubscribe(); }

		this.inflight = this.warehouseService.getPickOrder(this.orderno, this.stage).subscribe({
			next: (r: any) => {
				this.loading = false;
				this.inflight = null;
				if (!r || r.success !== true) {
					this.error = (r && r.message) ? r.message : 'Order did not load.';
					return;
				}
				this.error = '';
				this.header = r.header;
				this.totals = r.totals;
				this.lines = r.lines || [];
				if (r.settings) { this.settings = r.settings; }
				this.regroup();
				setTimeout(() => this.focusSink(), 60);
			},
			error: () => {
				this.loading = false;
				this.inflight = null;
				this.error = 'Order is not reachable.';
			},
		});
	}

	/** Bins are the walk path, so they are the grouping — outstanding bins first. */
	private regroup() {
		const map = new Map<string, any[]>();
		for (const l of this.lines) {
			const bin = (l.bin && `${l.bin}`.trim() !== '') ? `${l.bin}`.trim() : '—';
			if (!map.has(bin)) { map.set(bin, []); }
			map.get(bin).push(l);
		}

		const groups: BinGroup[] = [];
		map.forEach((lines, bin) => {
			groups.push({ bin, lines, done: lines.filter(l => l.complete).length });
		});

		groups.sort((a, b) => {
			const aOpen = a.done < a.lines.length;
			const bOpen = b.done < b.lines.length;
			if (aOpen !== bOpen) { return aOpen ? -1 : 1; }
			return a.bin.localeCompare(b.bin, undefined, { numeric: true });
		});

		this.binGroups = groups;
		this.recount();
	}

	/** Bin headers show a live done-count; the order of the groups stays put mid-pick. */
	private rescoreBins() {
		for (const g of this.binGroups) {
			g.done = g.lines.filter(l => l.complete).length;
		}
	}

	private recount() {
		let target = 0;
		let done = 0;
		let linesDone = 0;
		for (const l of this.lines) {
			target += +l.target;
			done += +l.done;
			if (l.complete) { linesDone++; }
		}
		this.totals = {
			...this.totals,
			line_count: this.lines.length,
			lines_done: linesDone,
			qty_pick: Math.round(target * 100) / 100,
			qty_picked: Math.round(done * 100) / 100,
			pct: target > 0 ? Math.round((done / target) * 100) : 0,
		};
	}

	get allPicked(): boolean {
		return this.lines.length > 0 && this.lines.every(l => l.complete);
	}

	get shortLines(): number {
		return this.lines.filter(l => l.short).length;
	}

	toggleDetail() {
		this.expandedDetail = !this.expandedDetail;
	}

	// ── Counting ─────────────────────────────────────────────────────────────

	/**
	 * The only write path. Optimistic locally, committed over the socket, and
	 * reconciled when the echo comes back — the server clamps anything past the
	 * stage's target, so the UI clamps the same way rather than inventing
	 * over-counts.
	 *
	 * Both server-side setters read the clamp off `item.ordered`, so the item
	 * sent with the job carries this stage's target there. On a check that is
	 * what the picker actually pulled, which is what stops a checker being asked
	 * to account for a short pick.
	 */
	private commit(line: any, delta: number) {
		const next = +line.done + delta;
		if (next < 0 || next > +line.target) { return; }

		line.done = Math.round(next * 10000) / 10000;
		line.remaining = Math.round(Math.max(0, +line.target - line.done) * 10000) / 10000;
		line.complete = line.done >= +line.target;
		line.partial = line.done > 0 && !line.complete;

		// Keep the underlying column in step so a re-read lines up with what the
		// screen already shows.
		if (this.stage === 'check') {
			line.loaded = line.done;
		} else {
			line.picked = line.done;
			// A covered line is no longer short, however thin the on-hand looked —
			// same rule the server uses when it decides notfillable.
			line.short = +line.qoh < +line.pick && !line.complete;
		}

		this.activePickId = line.pick_id;
		this.recount();
		this.rescoreBins();

		const data = {
			item: { ...line, ordered: line.target },
			count: delta,
			stockid: line.stockid,
			orderno: line.orderno,
			lineno: line.linenumber,
			pick_id: line.pick_id,
			pick: line.pick,
			user: this.me,
		};

		if (this.stage === 'check') {
			this.omsService.sendOrderCheckUpdate({ data, roomName: this.CHAT_ROOM }, () => {});
		} else {
			this.omsService.sendOrderPickUpdate({ data, roomName: this.CHAT_ROOM }, () => {});
		}
	}

	add(line: any, event?: any) {
		if (event) { event.stopPropagation(); }
		if (line.complete) { return; }
		if (this.stage === 'pick' && +line.done + 1 > +line.qoh && +line.qoh < +line.pick) {
			this.say(`${line.stockid}: only ${line.qoh} on hand`, 'warn');
		}
		this.commit(line, 1);
		this.buzz(20);
	}

	sub(line: any, event?: any) {
		if (event) { event.stopPropagation(); }
		if (+line.done <= 0) { return; }
		this.commit(line, -1);
		this.buzz(10);
	}

	pickAll(line: any, event?: any) {
		if (event) { event.stopPropagation(); }
		const delta = +line.target - +line.done;
		if (delta <= 0) { return; }
		const overStock = this.stage === 'pick' && +line.pick > +line.qoh;
		this.commit(line, delta);
		// The stock warning outranks the success note — a picker taking six off a
		// shelf the system thinks holds two needs to hear about it.
		if (overStock) {
			this.say(`${line.stockid}: only ${line.qoh} on hand — see a supervisor`, 'warn');
		} else {
			this.say(`${line.stockid} — ${line.target} ${this.doneWord}`, 'ok');
		}
		this.buzz(40);
	}

	clearLine(line: any, event?: any) {
		if (event) { event.stopPropagation(); }
		if (+line.done <= 0) { return; }
		this.commit(line, -+line.done);
		this.buzz(10);
	}

	linePct(line: any): number {
		return +line.target > 0 ? Math.min(100, Math.round((+line.done / +line.target) * 100)) : 0;
	}

	// ── Scanning ─────────────────────────────────────────────────────────────

	private get sink(): ElementRef {
		return this.mode === 'order' ? this.dwSinkOrder : this.dwSinkQueue;
	}

	private get manual(): ElementRef {
		return this.mode === 'order' ? this.manualInputOrder : this.manualInputQueue;
	}

	focusSink() {
		if (this.scanMode === 'scan' && this.sink) {
			this.sink.nativeElement.focus();
		}
	}

	toggleManual() {
		this.scanMode = this.scanMode === 'scan' ? 'manual' : 'scan';
		this.keyBuffer = '';
		setTimeout(() => {
			if (this.scanMode === 'manual' && this.manual) {
				this.manual.nativeElement.focus();
			} else {
				this.focusSink();
			}
		}, 50);
	}

	/**
	 * Scanners type fast and finish with Enter, but not every wedge sends one —
	 * so a short idle also commits the buffer.
	 */
	onSinkInput(event: any) {
		const value = `${event.target.value || ''}`;
		event.target.value = '';
		if (!value) { return; }

		this.keyBuffer += value;
		if (this.scanTimer) { clearTimeout(this.scanTimer); }

		if (this.keyBuffer.indexOf('\n') > -1 || this.keyBuffer.indexOf('\r') > -1) {
			const code = this.keyBuffer.replace(/[\r\n]/g, '');
			this.keyBuffer = '';
			this.handleScan(code);
			return;
		}

		this.scanTimer = setTimeout(() => {
			const code = this.keyBuffer;
			this.keyBuffer = '';
			this.handleScan(code);
			this.cdr.detectChanges();
		}, 120);
	}

	onManualEnter(event: any) {
		event.preventDefault();
		const code = `${event.target.value || ''}`.trim();
		event.target.value = '';
		if (code) { this.handleScan(code); }
	}

	handleScan(raw: string) {
		const code = `${raw || ''}`.trim().toUpperCase();
		if (code === '') { return; }
		this.lastScanned = code;

		// On the queue a scan is an order number — jump straight into it.
		if (this.mode === 'queue') {
			const row = this.rows.find(r => `${r.orderno}` === code.replace(/^0+/, ''));
			if (row) { this.openOrder(row); return; }
			if (/^\d+$/.test(code)) {
				this.router.navigate(['/warehouse/pick-station', code.replace(/^0+/, '')]);
				return;
			}
			this.say(`No order matches ${code}`, 'err');
			return;
		}

		const matches = this.lines.filter(l => (l.scan_keys || []).indexOf(code) > -1);

		if (!matches.length) {
			this.say(`${code} is not on this order`, 'err');
			this.buzz([30, 60, 30]);
			return;
		}

		// Same item across two bins: fill the first bin that still needs units.
		const line = matches.find(l => !l.complete) || matches[0];

		if (line.complete) {
			this.say(`${line.stockid} is already complete`, 'warn');
			this.buzz([20, 40, 20]);
			this.scrollToLine(line);
			return;
		}

		this.add(line);
		this.say(`${line.stockid} — ${line.done}/${line.target}${line.complete ? ' ✓' : ''}`, line.complete ? 'ok' : 'info');
		this.scrollToLine(line);
	}

	private scrollToLine(line: any) {
		this.activePickId = line.pick_id;
		setTimeout(() => {
			const el = document.getElementById('pl-' + line.pick_id);
			if (el && el.scrollIntoView) {
				el.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		}, 60);
	}

	private say(text: string, tone: string) {
		this.feedback = [{ text, tone }];
		if (this.feedbackTimer) { clearTimeout(this.feedbackTimer); }
		this.feedbackTimer = setTimeout(() => {
			this.feedback = [];
			this.cdr.detectChanges();
		}, 3200);
	}

	private buzz(pattern: any) {
		if (window.navigator && window.navigator.vibrate) {
			navigator.vibrate(pattern);
		}
	}

	// ── Claim / complete ─────────────────────────────────────────────────────

	claim(row: any, event?: any) {
		if (event) { event.stopPropagation(); }
		const release = row.claimed_user === this.me;
		this.warehouseService.claimPickOrder(row.orderno, this.me, release).subscribe((r: any) => {
			if (r && r.success) {
				row.claimed = !release;
				row.claimed_user = release ? '' : this.me;
			}
		});
	}

	/**
	 * Close the stage out, then ask the server whether this order invoices
	 * itself. The invoice decision is entirely the server's — it refuses a short
	 * pick, an unchecked order where checking is required, and anything already
	 * invoiced — so the station just relays what it says.
	 */
	completePick() {
		if (this.completing) { return; }
		this.completing = true;

		const data = { location: this.location, orderno: this.orderno };
		const close = this.stage === 'check'
			? this.ordersService.completeOrderCheck(data)
			: this.ordersService.completeOrderPick(data);

		close.subscribe({
			next: () => {
				this.omsService.sendOrderPickUpdate({ data: { user: this.user }, roomName: this.CHAT_ROOM }, () => {});
				this.warehouseService.claimPickOrder(this.orderno, this.me, true).subscribe(() => {});
				this.finalize();
			},
			error: () => {
				this.completing = false;
				this.say(`Could not close the ${this.stage} — try again`, 'err');
			},
		});
	}

	private finalize() {
		const orderno = this.orderno;

		if (!this.autoInvoiceArmed) {
			this.completing = false;
			this.backToQueue();
			return;
		}

		this.warehouseService.finalizeStage(orderno, this.stage).subscribe({
			next: (r: any) => {
				this.completing = false;
				if (r && r.invoiced) {
					this.flash(`#${orderno} invoiced${r.transno ? ' — ' + r.transno : ''}`, 'ok');
				} else if (r && r.reason && r.reason !== 'not_enabled') {
					// Not an error: the order closed fine, it just isn't invoicing
					// itself. Say why so nobody goes looking for a missing invoice.
					this.flash(`#${orderno}: ${r.message}`, 'warn');
				}
				this.backToQueue();
			},
			error: () => {
				this.completing = false;
				this.flash(`#${orderno} closed, but auto-invoice did not run`, 'warn');
				this.backToQueue();
			},
		});
	}

	/** Would a clean close-out on this stage try to invoice? Drives the button copy. */
	get autoInvoiceArmed(): boolean {
		return this.stage === 'check'
			? !!this.settings.auto_invoice_on_check
			: !!this.settings.auto_invoice_on_pick;
	}

	private flash(text: string, tone: string) {
		this.globalSearchService.showNotification(text, tone === 'ok' ? 'success' : 'warning', 'bottom', 'right');
	}

	// ── Printing ─────────────────────────────────────────────────────────────

	/**
	 * Two roads to paper, because the printer might be on either side of the
	 * room. A registered PrintConnect device for this stage gets the job pushed
	 * to it; with none configured we open the slip inline instead, which is the
	 * AirPrint path from a tablet paired to its own roll printer.
	 */
	printSlip(event?: any) {
		if (event) { event.stopPropagation(); }
		if (!this.orderno || this.printing) { return; }

		const printType = this.stage === 'check' ? 'CHECK' : 'PICK';
		const devices = this.usersService.getDevicesByType(this.me, printType);

		if (!devices || !devices.length) {
			window.open(this.slipUrl, '_blank');
			return;
		}

		this.printing = true;
		this.printService.printConnectPrint({
			include_pdf: false,
			user_printers: devices,
			id: this.orderno,
			roll_width: this.settings.roll_width || 80,
		}).subscribe({
			next: (r: any) => {
				this.printing = false;
				this.say(r && r.success ? 'Sent to the printer' : (r && r.message) || 'Print failed', r && r.success ? 'ok' : 'err');
			},
			error: () => {
				this.printing = false;
				this.say('Print failed — opening the slip instead', 'warn');
				window.open(this.slipUrl, '_blank');
			},
		});
	}

	get slipUrl(): string {
		const cfg = this.globalSearchService.configsubscription.value;
		const base = cfg && cfg.apiServer ? cfg.apiServer.baseUrl : '';
		const key = cfg && cfg.apiCredentials ? cfg.apiCredentials.clientId : '';
		return `${base}?route=apir/documents/pickroll&id=${encodeURIComponent(this.orderno)}`
			+ `&stage=${this.stage}&width=${this.settings.roll_width || 80}&display=inline&key=${key}`;
	}

	// ── Stage wording ────────────────────────────────────────────────────────
	// One place to name things, so the two stages read as their own screen
	// rather than a picking screen with the nouns swapped.

	get stageTitle(): string {
		return this.stage === 'check' ? 'Checking' : 'Picking';
	}

	get doneWord(): string {
		return this.stage === 'check' ? 'checked' : 'picked';
	}

	get scanHint(): string {
		return this.stage === 'check' ? 'Scan to check' : 'Scan an item';
	}

	get openLabel(): string {
		return this.stage === 'check' ? 'To Check' : 'To Pick';
	}

	get doneLabel(): string {
		return this.stage === 'check' ? 'Checked' : 'Picked';
	}

	get startLabel(): string {
		return this.stage === 'check' ? 'Start check' : 'Start pick';
	}

	get completeLabel(): string {
		if (this.completing) { return 'Closing…'; }
		if (!this.allPicked) { return 'Close short'; }
		if (this.autoInvoiceArmed) { return 'Complete & invoice'; }
		return this.stage === 'check' ? 'Complete check' : 'Complete pick';
	}

	get emptyLine(): string {
		return this.stage === 'check'
			? 'Nothing waiting to be checked. 🎉'
			: 'Nothing waiting to be picked. 🎉';
	}

	// ── Socket ───────────────────────────────────────────────────────────────

	/**
	 * Two pickers on one order (or a supervisor on the desktop screen) should see
	 * each other. The echo is debounced into one refetch so a fast scan run
	 * doesn't stack requests.
	 */
	private connect() {
		if (!this.me) { return; }
		this.omsService.setupSocketConnection(this.me);

		this.omsService.subscribeToOrderPicks((_err: any, msg: any) => {
			if (msg && msg.user && msg.user === this.me) { return; }
			if (this.echoTimer) { clearTimeout(this.echoTimer); }
			this.echoTimer = setTimeout(() => {
				if (this.mode === 'order') { this.loadOrder(true); } else { this.loadQueue(true); }
			}, 1500);
		});
	}

	// ── View helpers ─────────────────────────────────────────────────────────

	age(row: any): string {
		const m = row.age_minutes || 0;
		if (m < 60) { return `${m}m`; }
		const h = Math.floor(m / 60);
		return h < 48 ? `${h}h` : `${Math.floor(h / 24)}d`;
	}

	rowTone(row: any): string {
		if (row.late) { return 'late'; }
		if (row.short_lines > 0) { return 'short'; }
		if (row.asap) { return 'asap'; }
		if (row.bucket === 'ready') { return 'ready'; }
		return '';
	}

	trackByPickId(_i: number, l: any) {
		return l.pick_id;
	}

	trackByOrderno(_i: number, r: any) {
		return r.orderno;
	}

	trackByBin(_i: number, g: BinGroup) {
		return g.bin;
	}
}
